import { defineStore } from 'pinia'
import ouService, { OrganizationalUnit } from '../services/ou-service'
import userPreferencesService from '../services/user-preferences-service'

interface OUState {
  accessibleOUs: OrganizationalUnit[]
  activeOU: OrganizationalUnit | null
  activeOUCode: string | null
  defaultOUCode: string | null
  permissions: Record<string, string[]>
  loading: boolean
  initialized: boolean
  isSuperAdmin: boolean
}

const STORAGE_KEY = 'hris_active_ou'
const OUS_STORAGE_KEY = 'hris_accessible_ous'

// Promise to deduplicate concurrent initialization calls
let initPromise: Promise<void> | null = null

export const useOUStore = defineStore('ou', {
  state: (): OUState => ({
    accessibleOUs: [],
    activeOU: null,
    activeOUCode: localStorage.getItem(STORAGE_KEY),
    defaultOUCode: null,
    permissions: {},
    loading: false,
    initialized: false,
    isSuperAdmin: false,
  }),

  getters: {
    currentOU: (state) => state.activeOU,
    currentOUCode: (state) => state.activeOUCode,
    currentOUName: (state) => state.activeOU?.ou_name || '',
    hasMultipleOUs: (state) => state.accessibleOUs.length > 1,
    canSwitchOU: (state) => state.accessibleOUs.length > 1 || state.isSuperAdmin,

    /**
     * Check if user has specific permission in current OU
     * Note: Permission codes should be lowercase (e.g., 'view', 'create')
     */
    hasPermission:
      (state) =>
      (module: string, permission: string): boolean => {
        if (state.isSuperAdmin) return true
        const modulePerms = state.permissions[module]
        return modulePerms?.includes(permission) || modulePerms?.includes('*') || false
      },

    /**
     * Get all permissions for a module in current OU
     */
    getModulePermissions:
      (state) =>
      (module: string): string[] => {
        if (state.isSuperAdmin) return ['*']
        return state.permissions[module] || []
      },
  },

  actions: {
    /**
     * Initialize OU store - call on app start
     */
    async initialize() {
      // Already initialized
      if (this.initialized) return

      // If initialization is in progress, wait for it
      if (initPromise) {
        await initPromise
        return
      }

      // Try to load from localStorage first for faster UX
      const cachedOUs = localStorage.getItem(OUS_STORAGE_KEY)
      if (cachedOUs) {
        try {
          this.accessibleOUs = JSON.parse(cachedOUs)
        } catch {
          // Invalid cache, will be refreshed
        }
      }

      // Create promise for concurrent callers to wait on
      initPromise = this.fetchAccessibleOUs().finally(() => {
        initPromise = null
      })

      await initPromise
      this.initialized = true
    },

    /**
     * Fetch accessible OUs from API
     */
    async fetchAccessibleOUs() {
      this.loading = true
      try {
        const response = await ouService.getAccessibleOUs()

        if (response.status && response.data) {
          this.accessibleOUs = Array.isArray(response.data) ? response.data : []
          this.isSuperAdmin = response.meta?.is_super_admin || false
          this.defaultOUCode = response.meta?.default_ou_code || null

          // Cache in localStorage
          if (this.accessibleOUs.length > 0) {
            localStorage.setItem(OUS_STORAGE_KEY, JSON.stringify(this.accessibleOUs))
          }

          // Auto-select OU if not set or invalid
          await this.ensureValidActiveOU()
        }
      } catch (error: any) {
        console.error('Failed to fetch accessible OUs:', error?.message || error)
        // If cached data exists, use it
        const cachedOUs = localStorage.getItem(OUS_STORAGE_KEY)
        if (cachedOUs && this.accessibleOUs.length === 0) {
          try {
            this.accessibleOUs = JSON.parse(cachedOUs)
          } catch {
            // Invalid cache
          }
        }
      } finally {
        this.loading = false
      }
    },

    /**
     * Ensure active OU is valid, auto-select if needed
     * Priority: 1. Current active OU (if valid), 2. Default OU, 3. First available OU
     */
    async ensureValidActiveOU() {
      if (this.accessibleOUs.length === 0) {
        this.clearActiveOU()
        return
      }

      // Check if current active OU is still valid
      if (this.activeOUCode) {
        const isValid = this.accessibleOUs.some((ou) => ou.ou_code === this.activeOUCode)
        if (isValid) {
          await this.setActiveOU(this.activeOUCode, false)
          return
        }
      }

      // Try to use default OU if set and valid
      if (this.defaultOUCode) {
        const isDefaultValid = this.accessibleOUs.some((ou) => ou.ou_code === this.defaultOUCode)
        if (isDefaultValid) {
          await this.setActiveOU(this.defaultOUCode, false)
          return
        }
      }

      // Auto-select first available OU
      await this.setActiveOU(this.accessibleOUs[0].ou_code, false)
    },

    /**
     * Set active OU
     * @param ouCode - OU code to set as active
     * @param validate - Whether to validate access via API (default: true)
     */
    async setActiveOU(ouCode: string, validate: boolean = true) {
      // Find OU in accessible list
      const ou = this.accessibleOUs.find((o) => o.ou_code === ouCode)

      if (!ou && !this.isSuperAdmin) {
        console.error('OU not accessible:', ouCode)
        return false
      }

      // Validate via API if requested
      if (validate) {
        try {
          const response = await ouService.validateAccess(ouCode)
          if (!response.has_access) {
            console.error('OU access denied:', ouCode)
            return false
          }
        } catch (error: any) {
          console.error('Failed to validate OU access:', error?.message || error)
          // If validation fails but OU is in accessible list, allow it
          if (!ou) {
            return false
          }
        }
      }

      // Fetch OU details with permissions (optional - don't fail if this errors)
      try {
        const details = await ouService.getOUDetails(ouCode)
        if (details.status && details.data) {
          this.activeOU = details.data.ou
          this.activeOUCode = ouCode
          this.permissions = details.data.permissions || {}

          // Persist to localStorage
          localStorage.setItem(STORAGE_KEY, ouCode)

          return true
        }
      } catch (error: any) {
        console.error('Failed to fetch OU details:', error?.message || error)
      }

      // Fallback: set without detailed permissions
      this.activeOU = ou || { ou_code: ouCode, ou_name: ouCode }
      this.activeOUCode = ouCode
      this.permissions = {}
      localStorage.setItem(STORAGE_KEY, ouCode)

      return true
    },

    /**
     * Clear active OU
     */
    clearActiveOU() {
      this.activeOU = null
      this.activeOUCode = null
      this.permissions = {}
      localStorage.removeItem(STORAGE_KEY)
    },

    /**
     * Clear all cached data (local only)
     * Note: Server cache is cleared in auth-store logout before token is revoked
     */
    clearCache() {
      this.accessibleOUs = []
      this.activeOU = null
      this.activeOUCode = null
      this.permissions = {}
      this.initialized = false
      this.isSuperAdmin = false
      initPromise = null
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(OUS_STORAGE_KEY)
      // Server cache clearing is now handled in auth-store.logout() before token revocation
    },

    /**
     * Refresh OU data (call after role changes)
     */
    async refresh() {
      this.initialized = false
      initPromise = null
      await this.initialize()
    },

    /**
     * Set default OU for current user
     */
    async setDefaultOU(ouCode: string | null) {
      try {
        const response = await userPreferencesService.setDefaultOU(ouCode)
        if (response.status) {
          this.defaultOUCode = ouCode
          return true
        }
        return false
      } catch (error: any) {
        console.error('Failed to set default OU:', error?.message || error)
        return false
      }
    },

    /**
     * Set current active OU as default
     */
    async setCurrentAsDefault() {
      if (!this.activeOUCode) return false
      return this.setDefaultOU(this.activeOUCode)
    },

    /**
     * Clear default OU
     */
    async clearDefaultOU() {
      try {
        const response = await userPreferencesService.clearDefaultOU()
        if (response.status) {
          this.defaultOUCode = null
          return true
        }
        return false
      } catch (error: any) {
        console.error('Failed to clear default OU:', error?.message || error)
        return false
      }
    },
  },
})
